import React, { FC, useState, useEffect } from "react";
import { Link } from "gatsby";
import { AnchorLink } from "gatsby-plugin-anchor-links";
import { PageLogo } from "../logo/PageLogo";
import { Translate } from "@comps/shared/Translate";
import { usePopupContext } from "@/contexts/PopUp";
import { usePageContext } from "@/contexts/Page";
import { useLayout } from "@/hooks/Layout";

// component styles:
import "./index.scss";

export const Header: FC<HeaderProps> = ({ transition }) => {
  const { externalWebsites, externalInternational, getLocationBySlug } =
    useLayout();
  const { slug, content, language, type, updateLanguageNavigate } =
    usePageContext();
  const { showPopup } = usePopupContext();

  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [widgetInfo, setWidgetInfo] = useState<any>(null);
  const [menu, setMenu] = useState<boolean>(false);

  useEffect(() => {
    document.body.style.overflow = menu ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [menu]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const windowWidth = window.innerWidth;
      setIsScrolled(scrollTop > 0 || windowWidth < 1280 || !transition);
    };

    const handleResize = () => {
      const windowWidth = window.innerWidth;
      setIsScrolled(windowWidth < 1400 || !transition);
    };

    handleScroll();
    handleResize();
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [transition]);

  const allowedSlugs = [
    "san-francisco",
    "bellevue",
    "madrid",
    "dubai",
    "miami",
    "doha",
    "peru",
  ];

  const generateLink = (path: string) => {
    return language === "es" ? path : `/en${path}`;
  };

  let activeSections;
  const currentLocation = getLocationBySlug(slug);
  activeSections = currentLocation
    ? currentLocation.navbarContent.navbarRedirection
    : [];

  const laCarta = currentLocation ? currentLocation.section02 : null;
  const laCartaLink = laCarta && slug === "peru" ? laCarta.menuList[0].redirection : '';

  const initializeWidget = (info: any) => {
    if (!info.widgetSupplier) {
      console.error("No widget supplier assigned");
      return;
    }

    if (info.widgetSupplier[0] === "redirection") {
      console.log("User will be redirected outside the website...");
      window.open(info.widgetScriptUrl, "_blank");
      return;
    }

    showPopup(<div className="reservations" id="widget-container" />);
    setWidgetInfo(info);
  };

  useEffect(() => {
    const container = document.getElementById("widget-container");
    if (!container) return;
    while (container.firstChild) {
      console.log("Cleaning up the widget container...");
      container.removeChild(container.firstChild);
    }

    if (widgetInfo) {
      switch (widgetInfo.widgetSupplier[0]) {
        case "iframe":
          console.log("Loading iframe widget...");

          const scriptTagSrc = widgetInfo.tagScriptUrl;          
          const iframe = document.createElement("iframe");
          iframe.setAttribute("width", "384");
          iframe.setAttribute("height", "384");
          iframe.setAttribute("src", widgetInfo.widgetScriptUrl);
          iframe.setAttribute("frameborder", "0");
          container.appendChild(iframe);
          if( scriptTagSrc ) {
            const scriptTag = document.createElement("script");
            scriptTag.src = scriptTagSrc;
            container.appendChild(scriptTag);
          }
          break;
        case "opentable":
          console.log("Loading OpenTable widget...");

          const scriptSrc = widgetInfo.widgetScriptUrl;
          const existingScript = container.querySelector(
            `script[src="${scriptSrc}"]`
          );

          if (existingScript) {
            console.log("Removing existing script...");
            container.removeChild(existingScript);
          }

          const script = document.createElement("script");
          script.src = scriptSrc;
          script.async = true;
          container.appendChild(script);
          break;
        default:
          console.log("No widget supplier assigned");
          console.error(
            "Unknown widget supplier: ",
            widgetInfo.widgetSupplier[0]
          );
      }
    }
  }, [widgetInfo]);

  return (
    <header id="page-header">
      <div className={`header-element ${isScrolled ? "scrolled" : ""}`}>
        <div className="header-element__content">
          <PageLogo color="white" />

          <div className="header-nav-container">
            <nav
              className={`header-nav ${menu ? "active-menu" : ""}`}
              id="header-nav"
            >
              <ul className="nav-list">
                <>
                  {type !== "template" && !content.includes("Location") ? (
                    <>
                      <li
                        className={`header__language-switcher ${
                          isScrolled ? "scrolled" : ""
                        }`}
                      >
                        <div className="dropdown">
                          <button className="dropdown-button" disabled>
                            <Translate id="language" />
                          </button>
                          <div className="dropdown-content">
                            <button
                              onClick={() => updateLanguageNavigate("es")}
                              disabled={language === "es"}
                            >
                              <Translate id="spanish" />
                            </button>
                            <button
                              onClick={() => updateLanguageNavigate("en")}
                              disabled={language === "en"}
                            >
                              <Translate id="english" />
                            </button>
                          </div>
                        </div>
                      </li>
                      <li onClick={() => menu && setMenu(false)}>
                        <Link to="/peru" className={`nav-link`}>
                          Perú
                        </Link>
                      </li>
                      <li onClick={() => menu && setMenu(false)}>
                        <Link
                          to={generateLink("/about")}
                          className={`nav-link`}
                        >
                          <Translate id="about" />
                        </Link>
                      </li>
                      <li onClick={() => menu && setMenu(false)}>
                        <Link to={generateLink("/news")} className={`nav-link`}>
                          <Translate id="news" />
                        </Link>
                      </li>
                    </>
                  ) : (
                    <>
                      {activeSections.length > 0 ? 
                        slug === "peru" ? (
                          <>
                            {activeSections.map((redirection, index) => (
                              <li key={index} onClick={() => menu && setMenu(false)}>
                                {redirection.redirectionType.includes("inner") ? (
                                  <AnchorLink
                                    to={redirection.redirectionPath}
                                    className="nav-link"
                                  >
                                    {redirection.redirectionText}
                                  </AnchorLink>
                                ) : (
                                  <a
                                    href={redirection.redirectionUrl}
                                    rel="noopener noreferrer"
                                    className="nav-link"
                                    target="_blank"
                                  >
                                    {redirection.redirectionText}
                                  </a>
                                )}
                              </li>
                            ))}
                          </>
                        ) : (
                          <>
                            <li
                              className={`header__language-switcher ${
                                isScrolled ? "scrolled" : ""
                              }`}
                            >
                              <div className="dropdown">
                                <button className="dropdown-button" disabled>
                                  <Translate id="more" />
                                </button>
                                <div className="dropdown-content">
                                  {
                                    activeSections.map((redirection, index) => (
                                      <li key={index} onClick={() => menu && setMenu(false)}>
                                        {redirection.redirectionType.includes("inner") ? (
                                          <AnchorLink
                                            to={redirection.redirectionPath}
                                            className="nav-link-dropdown"
                                          >
                                            {redirection.redirectionText}
                                          </AnchorLink>
                                        ) : (
                                          <a
                                            href={redirection.redirectionUrl}
                                            rel="noopener noreferrer"
                                            className="nav-link-dropdown"
                                            target="_blank"
                                          >
                                            {redirection.redirectionText}
                                          </a>
                                        )}
                                      </li>
                                    )
                                  )}
                                </div>
                              </div>
                            </li>
                          </>
                        ) : (
                          <></>
                      )}
                    </>
                  )}

                  {slug === "peru" ? (
                    <>
                      <li onClick={() => menu && setMenu(false)}>
                        <Link
                          to={generateLink("/locations")}
                          className={`nav-link`}
                        >
                          <Translate id="locations" />
                        </Link>
                      </li>
                      <a
                        onClick={() => menu && setMenu(false)}
                        href={laCartaLink ? laCartaLink : ""}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mobile-hidden booking-link"
                      >
                        <button
                          type="button"
                          className="button style-primary-dark"
                        >
                          La Carta
                        </button>
                      </a>
                    </>
                  ) : (
                    <>
                      <li onClick={() => menu && setMenu(false)}>
                        <Link
                          to={generateLink("/locations")}
                          className={`nav-link`}
                        >
                          <Translate id="locations" />
                        </Link>
                      </li>
                      {currentLocation?.locationReservation ? (
                        currentLocation.locationReservation.locationReservationToggler && (
                            <li
                              onClick={() => {
                                initializeWidget(
                                  currentLocation.locationReservation
                                );
                                if (menu) setMenu(false);
                              }}
                            >
                              <Link
                                to={generateLink("/reservations")}
                                style={{ pointerEvents: "none" }}
                                className="booking-link button style-primary-dark"
                              >
                                <Translate id="reservations" />
                              </Link>
                            </li>
                          ) 
                        ) : (
                        <li onClick={() => menu && setMenu(false)}>
                          <Link
                            to={generateLink("/reservations")}
                            className="booking-link button style-primary-dark"
                          >
                            <Translate id="reservations" />
                          </Link>
                        </li>
                      )}
                    </>
                  )}
                </>
              </ul>

              {slug === "peru" ? (
                <div className="webs-cta-container">
                  <p className="webs-cta-container__presentation">
                    <Translate id="also_visit" />
                  </p>
                  <ul className="webs-cta-container__elements">
                    {externalWebsites.map((web, index) => (
                      <li className="element" key={index}>
                        <img
                          className="element__image"
                          src={web.websiteImage.node.publicUrl}
                          alt={web.websiteImage.node.altText}
                          title={web.websiteImage.node.title}
                        />
                        <a
                          className="element__redirection redirection-button"
                          rel="noopener noreferrer"
                          href={web.websiteRedirection}
                          target="_blank"
                        ></a>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : allowedSlugs.includes(slug) ? (
                <div className="webs-cta-container">
                  <p className="webs-cta-container__presentation">
                    <Translate id="also_visit" />
                  </p>
                  <ul className="webs-cta-container__elements">
                    {externalInternational.map((web, index) => (
                      <li className="element" key={index}>
                        <img
                          className="element__image"
                          src={web.websiteImage.node.publicUrl}
                          alt={web.websiteImage.node.altText}
                          title={web.websiteImage.node.title}
                        />
                        <a
                          className="element__redirection redirection-button"
                          rel="noopener noreferrer"
                          href={web.websiteRedirection}
                          target="_blank"
                        ></a>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </nav>
            <div
              className={`toggle-element ${menu ? "active" : ""}`}
              onClick={() => setMenu(!menu)}
              aria-controls="header-nav"
              aria-expanded={menu}
            ></div>
          </div>
        </div>
      </div>
    </header>
  );
};
